/**
 * WHAT A QUESTION FOUND, at the top of its page.
 *
 * A dig aimed at a question used to file findings under it and never say what
 * they added up to: seven findings that all said "Valant is an EHR" sat under
 * "Is Valant a BI tool or an EHR?", and it read as unanswered. The answerer now
 * writes the question's own answer while a dig is aimed at it, by the same
 * rules as the room's: it always takes a side, the board caps how sure it may
 * sound, and it cites the findings by number.
 */
import { useState } from 'react';
import type { BoardroomAnswer } from '../../../types';
import ItemRefText from '../ItemRefText';
import { cappedNote, movementWords, strengthTone, supportWords } from './answerStance';
import { feedTime, feedTimeTitle } from '../feedTime';

export default function QuestionAnswer({
  answer,
  versions,
  onOpen,
}: {
  answer: BoardroomAnswer | null;
  versions: BoardroomAnswer[];
  onOpen: (id: number) => void;
}) {
  const [showVersions, setShowVersions] = useState(false);
  if (!answer) {
    return (
      <section
        className="shrink-0 rounded-xl border border-dashed border-gray-300 bg-white p-3 text-xs text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
        data-testid="question-answer"
      >
        No answer yet. A dig aimed at this question writes one as its findings land, and keeps it up to date.
      </section>
    );
  }
  const support = supportWords(answer);
  const capped = cappedNote(answer);
  return (
    <section
      className="shrink-0 rounded-xl border border-indigo-200 bg-white p-3 dark:border-indigo-800 dark:bg-gray-800"
      data-testid="question-answer"
    >
      <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-gray-400">
        Answer
        <span className={`rounded px-1.5 py-0.5 normal-case tracking-normal ${strengthTone(answer.strength)}`}>
          {answer.strength}
        </span>
        {(support || capped) && (
          <span className="normal-case tracking-normal" title="What the answer rests on, counted">
            {[support, capped].filter(Boolean).join(' · ')}
          </span>
        )}
        <span className="ml-auto normal-case tracking-normal" title={feedTimeTitle(answer.created_at)}>
          {movementWords(answer)} · {feedTime(answer.created_at)}
        </span>
      </div>
      <p className="text-sm text-gray-900 dark:text-gray-100">
        <ItemRefText text={answer.stance} onOpen={onOpen} />
      </p>
      {answer.moved_by && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">What moved it: {answer.moved_by}</p>
      )}
      {answer.claims.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-gray-700 dark:text-gray-200">
          {answer.claims.map(c => (
            <li key={c.key} className="flex gap-1.5">
              <span className={c.side === 'against' ? 'text-amber-600' : 'text-emerald-600'}>
                {c.side === 'against' ? '−' : '+'}
              </span>
              <span>
                {c.text}{' '}
                {/* NUMBERED IN ORDER, not by item id: "#51447" means nothing to a reader. */}
                {c.receipt_ids.map((id, i) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onOpen(id)}
                    className="mr-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    [{i + 1}]
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
      {answer.would_change_it.length > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Would change it: {answer.would_change_it.map(w => w.text).join(' · ')}
        </p>
      )}
      {versions.length > 0 && (
        <div className="mt-2 text-xs">
          <button
            type="button"
            onClick={() => setShowVersions(v => !v)}
            className="text-gray-500 hover:underline dark:text-gray-400"
            data-testid="question-answer-versions"
          >
            {showVersions ? 'hide' : 'show'} {versions.length} earlier answer{versions.length === 1 ? '' : 's'}
          </button>
          {showVersions && (
            <ul className="mt-1 space-y-1 text-gray-500 dark:text-gray-400">
              {versions.map(v => (
                <li key={v.id}>
                  <span className="text-gray-400" title={feedTimeTitle(v.created_at)}>
                    {feedTime(v.created_at)} · {v.strength}:
                  </span>{' '}
                  {v.stance}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
